import { t, useLanguage } from '@/lib/i18n'
import { Award, BarChart3, BookOpen, CalendarRange, History, Home, Menu, Mic, Sparkles, Trophy, UserRound, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { AccountMenu } from '@/components/shell/AccountMenu'
import { Brand } from '@/components/shell/Brand'
import { HeaderSearch } from '@/components/shell/HeaderSearch'
import { useAuth } from '@/features/auth/auth-context'
import { NotificationsBell } from '@/features/notifications/NotificationsBell'
import { cn } from '@/lib/cn'

// Lucent navigation vocabulary (DESIGN-BRIEF: Focus · Practice · Evidence ·
// Plan) mapped onto the real, unrenamed routes; the remaining product surfaces
// keep their own entries.
const nav = [
  { to: '/home', label: 'Focus', icon: Home },
  { to: '/exams-library', label: 'Practice', icon: BookOpen },
  { to: '/keep-talking', label: 'Keep Talking', icon: Mic },
  { to: '/my-exams', label: 'Attempts', icon: History },
  { to: '/leaderboard', label: 'Community', icon: Trophy },
  { to: '/achievements', label: 'Achievements', icon: Award },
  { to: '/results', label: 'Evidence', icon: BarChart3 },
  { to: '/plan', label: 'Plan', icon: CalendarRange },
]

const mobileNav = nav.filter(({ label }) => ['Focus', 'Practice', 'Keep Talking', 'Evidence'].includes(label))

export function LucentShell() {
  useLanguage()
  const { demo } = useAuth()
  const { pathname } = useLocation()
  const menuRef = useRef<HTMLButtonElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const navId = useId()
  const [navOpen, setNavOpen] = useState(false)

  // Back and forward reach a new page without a click on the menu; leaving it
  // open would cover the page the student just moved to.
  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!navOpen) return
    // A press or a focus change anywhere but the menu and its button closes
    // it. Focus matters for keyboard students: they leave with Tab, not a
    // click, and the open panel sits over the search suggestions and the
    // account panel they tab on to.
    const closeIfOutside = (event: Event) => {
      const target = event.target as Node
      if (navRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setNavOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setNavOpen(false)
      menuRef.current?.focus()
    }
    // Widening the window past the collapse point hides the button and brings
    // the pill row back; an open state left behind would have no control, and
    // the panel would reappear already open on narrowing again.
    const onResize = () => {
      if (!menuRef.current?.getClientRects().length) setNavOpen(false)
    }
    window.addEventListener('pointerdown', closeIfOutside)
    window.addEventListener('focusin', closeIfOutside)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('pointerdown', closeIfOutside)
      window.removeEventListener('focusin', closeIfOutside)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onResize)
    }
  }, [navOpen])

  return (
    <div className="lucent-shell">
      {/* Below the collapse breakpoint in index.css the pill row folds behind
          the menu button (IELTS-583). The button comes before the list it
          opens so Tab walks straight into it. */}
      <header className="product-header" data-nav-open={navOpen || undefined}>
        <Brand />
        <button
          ref={menuRef}
          type="button"
          className="product-header__menu"
          aria-label={t("Menu")}
          aria-expanded={navOpen}
          aria-controls={navId}
          onClick={() => setNavOpen((current) => !current)}
        >
          {navOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
          <span aria-hidden="true">{t("Menu")}</span>
        </button>
        {/* A click on the page already open changes no route, so choosing any
            entry closes the menu here rather than waiting for the pathname. */}
        <nav
          ref={navRef}
          id={navId}
          aria-label={t("Primary navigation")}
          onClick={(event) => {
            if ((event.target as Element).closest('a')) setNavOpen(false)
          }}
        >
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => cn(isActive && 'active')}>
              <Icon size={16} aria-hidden="true" />
              {t(label)}
            </NavLink>
          ))}
        </nav>
        <div className="product-header__actions">
          <HeaderSearch />
          {demo && <span className="demo-chip"><Sparkles size={13} />{t(" Sample journal · nothing is saved")}</span>}
          <NotificationsBell />
          {/* Profile and sign out both live behind the avatar. Two sign-out
              affordances a few pixels apart is how one of them gets clicked by
              accident, and an unlabelled icon is how neither gets found. */}
          <AccountMenu />
        </div>
      </header>
      <main className="product-main"><Outlet /></main>
      <nav className="product-dock" aria-label={t("Mobile navigation")}>
        {mobileNav.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => cn(isActive && 'active')}>
            <Icon size={18} /><span>{t(label)}</span>
          </NavLink>
        ))}
        <NavLink to="/profile"><UserRound size={18} /><span>{t("Profile")}</span></NavLink>
      </nav>
    </div>
  )
}
